import { CurrencyPipe, JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { pieceRateUsage, StoredRateField } from '@/app/common/brief-pieces/piece-rate-usage';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { HasRoleDirectiveDirective } from '@/app/common/directives/has-role/has-role-directive.directive';
import { FormatService } from '@/app/common/format/format.service';
import { SubmittedPostVersionEnum } from '@/app/common/general-types/briefs.dto';
import { BriefService } from '@/app/services/brief/brief.service';
import { ManageBriefTabsService } from '../../../../service/manage-brief-tabs/manage-brief-tabs.service';
import { payView } from '../../briefing.helpers';
import { ResultsVersionSelect } from '../results-version-select/results-version-select';

/**
 * The admin-only cards (roles 4 / 6): Pricing Details, Noodle Details, Results Type Details.
 * Moved out of the old Briefing tab unchanged; the redesign hand-over leaves them out of scope.
 */
@Component({
  selector: 'mbt-briefing-admin',
  imports: [HasRoleDirectiveDirective, JsonPipe, CurrencyPipe, ResultsVersionSelect],
  templateUrl: './briefing-admin.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingAdmin {
  readonly manageBriefTabsService = inject(ManageBriefTabsService);
  readonly formatService = inject(FormatService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly briefService = inject(BriefService);
  private readonly toastNotificationService = inject(ToastNotificationService);

  readonly SubmittedPostVersionEnum = SubmittedPostVersionEnum;

  readonly expandPricingDetails = signal(false);
  readonly expandNoodleDetails = signal(false);
  readonly expandResultsTypeDetails = signal(false);
  readonly sendingToNoodle = signal(false);

  /** Lowest and highest per-video base price (major units); equal on a legacy brief. */
  readonly basePriceRange = computed<{ min: number; max: number }>(() => {
    const brief = this.manageBriefTabsService.focussedBrief();
    if (!brief) {
      return { min: 0, max: 0 };
    }
    const view = payView(brief);
    return { min: view.baseMin, max: view.baseMax };
  });

  /** Whether a stored rate is part of this brief's price, for the Calculations highlight. */
  rateUsed(field: StoredRateField): boolean {
    const brief = this.manageBriefTabsService.focussedBrief();
    if (brief?.pieces?.length) {
      return pieceRateUsage(brief.pieces, brief.currentPrice).used.has(field);
    }
    return (brief?.currentPrice?.[field] ?? 0) > 0;
  }

  getAudiencePricePerVideo(bonusValue: number): number {
    return this.basePriceRange().min + bonusValue / 100;
  }

  /** Matches the role-77 Current Price column. */
  showPricingDetailsThirdColumn(): boolean {
    return this.currentUserService.hasAnyRole([77]);
  }

  private copy(value: string | undefined, label: string): void {
    if (!value) {
      return;
    }
    void navigator.clipboard.writeText(value);
    void this.toastNotificationService.alert({ title: 'Copied!', message: `${label} copied to clipboard`, type: 'success', icon: 'success' });
  }

  copyCurrentPriceId(): void {
    this.copy(this.manageBriefTabsService.focussedBrief()?.currentPrice?.id, 'Current price ID');
  }

  copyPricingModelId(): void {
    this.copy(this.manageBriefTabsService.focussedBrief()?.basePricingModel?.id, 'Pricing model ID');
  }

  checkSentToNoodle(): boolean {
    return this.manageBriefTabsService.focussedBrief()?.noodleBriefStatus === 'Active';
  }

  async sendToNoodle(): Promise<void> {
    const id = this.manageBriefTabsService.focussedBrief()?.id;
    if (!id) {
      return;
    }
    this.sendingToNoodle.set(true);
    try {
      await firstValueFrom(this.briefService.sendBriefToNoodle(id));
      void this.toastNotificationService.alert({ title: 'Success!', message: 'Brief re-sent to Noodle', type: 'success', icon: 'success' });
    } catch {
      void this.toastNotificationService.alert({ title: 'Error!', message: 'Error re-sending brief to Noodle', type: 'error', icon: 'error' });
    } finally {
      this.sendingToNoodle.set(false);
    }
  }
}
